import { useEffect, useState, useRef, ChangeEvent } from "react";
import { Button } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";

import "./style.scss";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ReactComponent as CubeIcon } from "../../../assets/cube.icon.svg";
import { createKnowledgeBase } from "../../../api/admin/knowledge-base/endpoints-referrers";
import { KnowledgeBase } from "../../../common/interfaces/admin/knowledge-base.interface";
import { LoaderStep } from "../../../common/enums/loader-step.enum";
import { Loader } from "../../../components/loader/loader.comp";

const AddKnowledgeBaseView = ({
    handleClose,
    handleSuccess
}: {
    handleClose: () => void;
    handleSuccess: (arg1: KnowledgeBase) => void;
}) => {
    const { user } = FirebaseAuthContainer.useContainer();
    const [kbName, setKBName] = useState<string>("");
    const [step, setStep] = useState<LoaderStep>(LoaderStep.start);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    });

    const handleKBNameChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setKBName(val ?? "");
    };

    const handleCloseClick = () => {
        handleClose();
    };

    const handleCreateClick = () => {
        if (!user || !kbName) return;

        setStep(LoaderStep.loading);
        createKnowledgeBase(user.companyId, kbName)
            .then(response => {
                setStep(LoaderStep.complete);
                handleSuccess(response.data);
            })
            .catch(error => {
                switch (error.code) {
                    case "ERR_NETWORK":
                        setErrorMessage("Network error.");
                        break;
                    default:
                        setErrorMessage("Something went wrong. Please try again later.");
                }
                setStep(LoaderStep.error);
            });
    };

    return (
        <div className="add-knowledge-base">
            {step === LoaderStep.start && (
                <>
                    <div className="add-knowledge-base-title">
                        <SvgIcon
                            className="add-knowledge-base-title__icon"
                            component={CubeIcon}
                            inheritViewBox
                        />
                        <span
                            className="add-knowledge-base-title__text"
                            role="heading"
                            aria-level={1}
                        >
                            Add Assistant
                        </span>
                    </div>
                    <div className="add-knowledge-base-data">
                        <input
                            ref={inputRef}
                            type="text"
                            aria-label="Assistant name"
                            placeholder="New Assistant name (you can change it later)"
                            onChange={handleKBNameChange}
                            value={kbName}
                        />
                    </div>
                </>
            )}
            {step === LoaderStep.loading && (
                <div className="add-knowledge-base-loading">
                    <div className="add-knowledge-base-loading-title">
                        <Loader
                            className="add-knowledge-base-loading-title__spinner"
                            size={40}
                            borderWidth={5}
                        />
                        <span className="add-knowledge-base-loading-title__text">
                            Creation in Progress
                        </span>
                    </div>
                </div>
            )}
            {step === LoaderStep.complete && (
                <div className="add-knowledge-base-complete">
                    <div className="add-knowledge-base-complete-title">
                        <SvgIcon
                            className="add-knowledge-base-complete-title__icon"
                            component={CubeIcon}
                            inheritViewBox
                        />
                        <span className="add-knowledge-base-complete-title__text">
                            Assistant Added
                        </span>
                    </div>
                </div>
            )}
            {step === LoaderStep.error && (
                <div className="add-knowledge-base-error">
                    <div className="add-knowledge-base-error-title">
                        <SvgIcon
                            className="add-knowledge-base-error-title__icon"
                            component={CubeIcon}
                            inheritViewBox
                        />
                        <span className="add-knowledge-base-error-title__text">Error</span>
                    </div>
                    {errorMessage && (
                        <span className="add-knowledge-base-error-description">{errorMessage}</span>
                    )}
                </div>
            )}
            <div className="add-knowledge-base-action">
                {(step === LoaderStep.start || step === LoaderStep.loading) && (
                    <Button
                        className="primary-button"
                        variant="text"
                        onClick={handleCreateClick}
                        disabled={!kbName || step === LoaderStep.loading}
                    >
                        {step === LoaderStep.start ? "Create" : "Please wait"}
                    </Button>
                )}
                {(step === LoaderStep.complete || step === LoaderStep.error) && (
                    <Button variant="text" className="primary-button" onClick={handleCloseClick}>
                        Close
                    </Button>
                )}
            </div>
        </div>
    );
};

export default AddKnowledgeBaseView;
